import {
  BookingState,
  Menu,
  PendingBooking,
  PendingBookingContact,
  Prepayment,
  SelectedMenu,
} from "../types";
import { loadStripe } from "@stripe/stripe-js/pure";
import { Stripe } from "@stripe/stripe-js";
import i18n from "../i18n";
import { numberFormat } from "../utils";
import { moduleSettings } from "./module-settings.store";
import { getLanguageSwitcherService } from "../services/language-switcher.service";
import moment from "moment";
import { commercialsStore } from "./commercials.store";
import { selectionStore } from "./selection.store";
class PendingBookingStore {
  private _pendingBooking: PendingBooking = {} as PendingBooking;
  private _stripeToken?: string | null | undefined;
  private _acceptTerms: boolean = false;
  private _acceptAllergiesTerms: boolean = false;

  set acceptAllergiesTerms(accept: boolean) {
    this._acceptAllergiesTerms = accept;
  }

  get acceptAllergiesTerms() {
    return this._acceptAllergiesTerms;
  }

  set acceptTerms(accept: boolean) {
    this._acceptTerms = accept;
  }

  get acceptTerms() {
    return this._acceptTerms;
  }

  set pendingBooking(newPendingBooking: PendingBooking) {
    this._pendingBooking = newPendingBooking;
  }

  get persons(): number {
    return this.pendingBooking.persons;
  }

  get children(): number {
    return this.pendingBooking.children;
  }

  resetBookingState() {
    this.pendingBooking.booking_state = BookingState.validated;
  }

  get bookingState(): string | undefined {
    if (this.pendingBooking) {
      return this.pendingBooking.booking_state;
    }
    return undefined;
  }

  public isWaitingState(): Boolean {
    if (this && this.pendingBooking && this.bookingState) {
      return this.bookingState === BookingState.waiting;
    }
    return false;
  }
  set note(noteInfo: string) {
    this.pendingBooking.note = noteInfo;
  }

  set stripeToken(token: string | null | undefined) {
    this._stripeToken = token;
  }

  get stripeToken(): string | null | undefined {
    return this._stripeToken;
  }

  get stripePromise(): PromiseLike<Stripe | null> | undefined {
    let prepayment = this.pendingBooking.prepayment;
    if (prepayment) {
      if (prepayment.stripe_account !== null) {
        let stripeAccountInfo = {
          stripeAccount: prepayment.stripe_account,
        };
        return loadStripe(
          this.pendingBooking.prepayment.stripe_key,
          stripeAccountInfo
        );
      } else {
        return loadStripe(this.pendingBooking.prepayment.stripe_key);
      }
    }
  }

  get pendingBooking(): PendingBooking {
    return this._pendingBooking;
  }

  get pendingBookingContact(): PendingBookingContact {
    return this._pendingBooking.contact_info;
  }

  set pendingBookingContact(contactInfo: PendingBookingContact) {
    this._pendingBooking.contact_info = contactInfo;
  }

  public getPendingBookingMessage(lang: string): string | null {
    const locale = getLanguageSwitcherService().golLocaleFromSwitcher(lang);
    const localeRestaurant = moduleSettings.getDefaultLocale();
    const note =
      this._pendingBooking.bookings_detail?.encoded_note ??
      this._pendingBooking.bookings_detail?.note ??
      null;
    if (note) {
      const _note = note[locale] || note[localeRestaurant] || note["en"];
      return decodeURIComponent(_note);
    }
    return null;
  }

  public get prepayment(): Prepayment {
    return this._pendingBooking.prepayment;
  }

  public set prepayment(prepayment: Prepayment) {
    this._pendingBooking.prepayment = prepayment;
  }

  public getPendingBookingPrepaymentKind(): string {
    let paymentKind = this.prepayment.payment_kind;
    return paymentKind === "guarantee" ? "deposit" : "down_payment";
  }

  public getPendingBookingPrepaymentCurrency(): string {
    return this.prepayment.currency;
  }

  public getPrepaymentPriceWithPrecision(): number | undefined {
    if (!this.prepayment?.amount) {
      return 0;
    }
    let amount = this.prepayment.amount;
    if (amount) {
      return amount / 100;
    }
  }

  public getIsVoucherEnabled(): boolean {
    return this._pendingBooking.voucher_enabled;
  }

  public getIsVoucherAllowed(): boolean {
    return this._pendingBooking.allow_vouchers ?? true;
  }

  public getBookingState(): BookingState {
    return this._pendingBooking.booking_state;
  }

  public get prepaymentInfo(): string | undefined {
    if (!this.prepayment) {
      return;
    }
    let priceWithPrecision = this.getPrepaymentPriceWithPrecision();
    if (priceWithPrecision) {
      let priceAndCurrency = numberFormat(
        i18n.language,
        priceWithPrecision,
        this.getPendingBookingPrepaymentCurrency()
      );
      const delay = moduleSettings.delayBeforeDepositCapture;
      const daysToRemove = Math.floor(delay / 24);
      const hoursToRemove = Math.floor(delay % 24);
      const dateWithTime = new Date(
        this.pendingBooking.date + "T" + this.pendingBooking.hour + ":00"
      );
      const hours = dateWithTime.getHours() - hoursToRemove;
      dateWithTime.setHours(hours);
      let delayedDate = moment(dateWithTime)
        .add(-1 * daysToRemove, "days")
        .locale(i18n.language);
      let date = delayedDate.format("LL");
      let time = delayedDate.format("LT");
      const translationKey =
        this.getPendingBookingPrepaymentKind() == "deposit"
          ? delay > 0
            ? "deposit"
            : "deposit_without_delay"
          : "down_payment";
      let prepaymentInfos = i18n.t(
        `instabook.prepayment.intro_text.${translationKey}`,
        {
          amount: priceAndCurrency,
          date: date,
          time: time,
        }
      );
      prepaymentInfos += "<br>";
      prepaymentInfos += i18n.t("instabook.prepayment.intro_text_2");
      return prepaymentInfos;
    }
  }
  get countryCode(): string | undefined {
    return this._pendingBooking.country_code;
  }
  public get selectedMenus(): Array<SelectedMenu> {
    return this._pendingBooking.selected_menus ?? [];
  }

  get statisfySameMenusPaxCounterMandatoryForRaiseBtn(): boolean {
    return this.sameNumberMenu
      ? !this.statisfySameMenusPaxCounterMandatory
      : true;
  }

  get statisfySameMenusPaxCounterMandatory(): boolean {
    return (
      this.selectedMenuCounter === this.persons ||
      selectionStore.menusCount == this.persons
    );
  }

  public get selectedMenuCounter(): number {
    return this.selectedMenus.reduce(
      (counter: number, menu: SelectedMenu) =>
        (counter = counter + menu.quantity),
      0
    );
  }

  public set selectedMenus(selectedMenus) {
    this._pendingBooking.selected_menus = selectedMenus;
  }

  get selectedMenuForAllGuests(): SelectedMenu | null {
    return (
      this.selectedMenus.filter((menu) => menu.for_all_guests === true)[0] ??
      null
    );
  }

  public get selectedVouchers() {
    return this._pendingBooking.vouchers;
  }

  public get selectedOffer() {
    return this._pendingBooking.offer;
  }

  get menuSelectionRequested(): boolean {
    return this._pendingBooking.menu_selection_requested ?? false;
  }

  get statisfyMenusMandatory(): boolean {
    return !this.menuSelectionRequested
      ? true
      : this.sameNumberMenu
      ? this.statisfySameMenusPaxCounterMandatory
      : this.selectedMenus.length > 0;
  }
  get sameNumberMenu(): boolean {
    return this._pendingBooking.same_number_menu ?? false;
  }
  get skip_prepayment_for_contact(): boolean {
    return this.pendingBooking.skip_prepayment_for_contact ?? false;
  }

  get duration(): number {
    return this.pendingBooking.duration ?? 0;
  }

  get showPptForm(): boolean {
    return this.skip_prepayment_for_contact
      ? false
      : !moduleSettings.hasSkippingGroupPpt;
  }

  public get selectedMenusIds(): Array<number> {
    return this.selectedMenus.map((selectedMenu: SelectedMenu) =>
      Number(selectedMenu.commercial_id)
    );
  }

  public setMenuIdsAvailiable(
    menus: Array<Menu>,
    menuIds: Array<number>
  ): { [key: number]: boolean } {
    return Object.assign(
      {},
      ...menus.map((menu: Menu) => ({ [menu.id]: menuIds.includes(menu.id) }))
    );
  }

  public get menusAvailabilities(): { [key: number]: boolean } {
    if (pendingBookingStore.selectedMenus.length === 0)
      return Object.assign(
        {},
        ...commercialsStore.menus.map((menu: Menu) => ({ [menu.id]: true }))
      );

    //if pendingBooking selectedMenus include allForGuest
    let _selectedMenuForAllGuests = this.selectedMenuForAllGuests;
    if (_selectedMenuForAllGuests !== null) {
      //set individual menu to false
      //and forAllGuestsMenus !== currentSelected menu to false
      return this.setMenuIdsAvailiable(commercialsStore.menus, [
        Number(_selectedMenuForAllGuests.commercial_id),
      ]);
    }
    //set allForGuestsMenu to false
    let individualMenuIds: Array<number> = commercialsStore.individualMenus.map(
      (menu: Menu) => {
        return menu.id;
      }
    );
    return this.setMenuIdsAvailiable(commercialsStore.menus, individualMenuIds);
  }

  public set recaptchaValue(recaptchaValue: string) {
    this._pendingBooking.recaptchaValue = recaptchaValue;
  }
}

export const pendingBookingStore = new PendingBookingStore();
